/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubscriptionDetail } from '@features/subscription/hooks/useSubscriptionDetail';
import { usePaymentMethodSession } from '@features/subscription/hooks/usePaymentMethodSession';
import PaymentMethodIframe from '@features/subscription/components/PaymentMethodIframe';
import PaymentFormSkeleton from '@features/subscription/components/PaymentFormSkeleton';
import { CardListSkeleton } from '@/shared/ui/components/basic/data-state/skeletons';
import { Text, UnstyledButton } from '@/shared/ui';
import { Modal } from '@/shared/ui/components/basic/Modal';
import { BlockMid, SettingsBlock } from '@/shared/ui/components/composed/settings-block';

const UNCHANGEABLE_STATUSES = new Set(['cancelled', 'expired']);

interface Props {
  agencyId: string;
  /**
   * Hosted inside a modal that already carries the title: the block drops
   * its own heading and says the useful rest — holder, cycle, next charge.
   */
  framed?: boolean;
  /**
   * Renders as the billing line inside the plan block's left column rather
   * than as a block of its own, which is where the approved prototype puts
   * it: the price, how it is paid and when it renews are one thought.
   */
  inline?: boolean;
  /** Already-localized "next charge on <date>" tail for the inline line. */
  renewalNote?: string;
}

const CARD =
  'border border-(--border-subtle) bg-(--surface-secondary) p-(--space-5) rounded-(--radius-lg) ' +
  'transition-[border-color,background-color] duration-[var(--transition-standard)]';

/**
 * Renders the Visa brand logo as an inline SVG.
 *
 * @param props - The accessible label for the logo.
 * @returns The Visa logo SVG.
 */
function VisaLogo({ ariaLabel }: { ariaLabel: string }) {
  return (
    <svg
      viewBox="0 0 48 16"
      width="48"
      height="16"
      aria-label={ariaLabel}
      role="img"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect width="48" height="16" rx="3" fill="#1a1f71" />
      <text
        x="24"
        y="12"
        textAnchor="middle"
        fill="#fff"
        fontFamily="Arial, sans-serif"
        fontSize="10"
        fontWeight="700"
        letterSpacing="1"
      >
        VISA
      </text>
    </svg>
  );
}

/**
 * Renders the Mastercard brand logo as an inline SVG.
 *
 * @param props - The accessible label for the logo.
 * @returns The Mastercard logo SVG.
 */
function MastercardLogo({ ariaLabel }: { ariaLabel: string }) {
  return (
    <svg
      viewBox="0 0 48 30"
      width="48"
      height="30"
      aria-label={ariaLabel}
      role="img"
      xmlns="http://www.w3.org/2000/svg"
    >
      <circle cx="18" cy="15" r="12" fill="#eb001b" />
      <circle cx="30" cy="15" r="12" fill="#f79e1b" />
      <path
        d="M24 6.27a12 12 0 0 1 0 17.46A12 12 0 0 1 24 6.27z"
        fill="#ff5f00"
      />
    </svg>
  );
}

/**
 * Selects the appropriate brand logo for a card, rendering a generic labeled
 * badge for unknown brands.
 *
 * @param props - The card brand name.
 * @returns The brand logo or a fallback badge.
 */
export function CardBrandLogo({ brand }: { brand: string }) {
  const { t } = useTranslation('features/pricing');
  const normalized = brand.toLowerCase().replace(/\s+/g, '');
  const label = brand.charAt(0).toUpperCase() + brand.slice(1).toLowerCase();

  if (normalized === 'visa') {
    return (
      <div data-testid="card-brand-logo" data-brand="visa">
        <VisaLogo ariaLabel={t('subscription.payment.cardBrand.visa')} />
      </div>
    );
  }

  if (normalized === 'mastercard') {
    return (
      <div data-testid="card-brand-logo" data-brand="mastercard">
        <MastercardLogo ariaLabel={t('subscription.payment.cardBrand.mastercard')} />
      </div>
    );
  }

  return (
    <div
      data-testid="card-brand-logo"
      data-brand={normalized}
      className="flex items-center justify-center rounded-(--radius-md)"
      style={{
        height: 'calc(var(--space-4) * 2.5)',
        width: 'calc(var(--space-4) * 3.5)',
        fontSize: 'var(--text-meta)',
        fontWeight: 700,
        border: '1px solid var(--border-subtle)',
        background: 'var(--surface-primary)',
        color: 'var(--text-secondary)',
      }}
    >
      {label}
    </div>
  );
}


/** One labeled fact: label on the left, the answer on the right. */
function FactRow({ label, value, last }: { label: string; value: string; last?: boolean }) {
  return (
    <div
      style={{
        display: 'flex', justifyContent: 'space-between', gap: 'var(--space-4)',
        padding: 'var(--space-2) 0',
        borderBottom: last ? 'none' : '1px solid var(--border-ultra-subtle)',
      }}
    >
      <Text component="span" style={{ fontSize: 'var(--text-small)', color: 'var(--text-secondary)' }}>
        {label}
      </Text>
      <Text
        component="span"
        style={{ fontSize: 'var(--text-small)', fontWeight: 'var(--font-bold)', color: 'var(--text-primary)' }}
      >
        {value}
      </Text>
    </div>
  );
}

/**
 * Displays payment method info from the subscription detail.
 */
export function SubscriptionPaymentCard({ agencyId, inline, renewalNote, framed }: Props) {
  const { t, i18n } = useTranslation('features/pricing');
  const { data, isLoading, refetch } = useSubscriptionDetail(agencyId);
  const session = usePaymentMethodSession(agencyId);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [banner, setBanner] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  if (isLoading) {
    return (
      <SettingsBlock title={t('subscription.payment.title')} testId="subscription-payment-block">
        <BlockMid>
          <div data-testid="payment-card-skeleton">
            <CardListSkeleton count={1} />
          </div>
        </BlockMid>
      </SettingsBlock>
    );
  }

  const paymentMethod = data?.payment_method ?? null;
  const canChangePayment = !UNCHANGEABLE_STATUSES.has(data?.subscription_status ?? '');

  /**
   * Opens the dialog on the click itself and mints the session behind it.
   *
   * The session is a network round trip and the framed form is another one;
   * waiting for both before showing anything left the click looking ignored.
   * The dialog animates open right away and the placeholder inside covers
   * both waits, so there is no moment without feedback.
   */
  const handleChangePayment = () => {
    setBanner(null);
    setIsModalOpen(true);
    void session.createSession();
  };

  const handleSuccess = () => {
    setIsModalOpen(false);
    session.reset();
    refetch();
    setBanner({ type: 'success', message: t('subscription.payment.updateSuccess') });
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    session.reset();
  };

  const renderModalBody = () => {
    if (session.isError) {
      return (
        <div
          data-testid="payment-session-error-modal"
          className="flex flex-col items-start gap-(--space-4) p-(--space-5)"
          style={{ minHeight: 360 }}
        >
          <Text style={{ margin: 0, fontSize: 'var(--text-body-sm)', color: 'var(--status-error-text)' }}>
            {t('subscription.payment.sessionError')}
          </Text>
          <UnstyledButton
            type="button"
            data-testid="retry-payment-session-btn"
            onClick={() => { void session.createSession(); }}
            style={{
              background: 'none', border: 'none', padding: 0,
              fontSize: 'var(--text-meta)', fontWeight: 600, color: 'var(--seed-base)',
              textDecoration: 'underline', textUnderlineOffset: '3px', cursor: 'pointer',
              fontFamily: 'var(--font-family)',
            }}
          >
            {t('subscription.payment.retry')}
          </UnstyledButton>
        </div>
      );
    }

    if (!session.formUrl) return <PaymentFormSkeleton />;

    return (
      <PaymentMethodIframe
        formUrl={session.formUrl}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
        onExpired={handleExpired}
      />
    );
  };

  const handleExpired = () => {
    setIsModalOpen(false);
    session.reset();
    setBanner({ type: 'error', message: t('subscription.payment.sessionExpired') });
  };

  if (inline) {
    return (
      <>
        <Text style={{ margin: 0, fontSize: 'var(--text-body-sm)', color: 'var(--text-secondary)' }}>
          {[
            paymentMethod
              ? t('subscription.payment.cardEnding', { lastFour: paymentMethod.last_four })
              : t('subscription.payment.noMethod'),
            renewalNote,
          ].filter(Boolean).join(' · ')}
        </Text>
        {banner && (
          <Text
            data-testid="payment-card-banner"
            style={{
              margin: 0, fontSize: 'var(--text-meta)',
              color: banner.type === 'success' ? 'var(--status-success-text)' : 'var(--status-error-text)',
            }}
          >
            {banner.message}
          </Text>
        )}
        {canChangePayment && (
          <UnstyledButton
            type="button"
            disabled={session.isLoading}
            data-testid="change-payment-btn"
            onClick={handleChangePayment}
            style={{
              alignSelf: 'flex-start', background: 'none', border: 'none', padding: 0,
              fontSize: 'var(--text-meta)', fontWeight: 600, color: 'var(--seed-base)',
              textDecoration: 'underline', textUnderlineOffset: '3px', cursor: 'pointer',
              fontFamily: 'var(--font-family)',
            }}
          >
            {t('subscription.payment.changePayment')}
          </UnstyledButton>
        )}
        {session.isError && (
          <Text data-testid="payment-session-error" style={{ margin: 0, fontSize: 'var(--text-meta)', color: 'var(--status-error-text)' }}>
            {t('subscription.payment.sessionError')}
          </Text>
        )}
        <Modal
          opened={isModalOpen}
          onClose={handleCancel}
          title={t('subscription.payment.iframeTitle')}
          size="5xl"
        >
          {renderModalBody()}
        </Modal>
      </>
    );
  }

  const money = (cents: number) => new Intl.NumberFormat(i18n.language, {
    style: 'currency', currency: 'BRL',
  }).format(cents / 100);
  const yearlyCycle = data?.billing?.billing_frequency === 'yearly';
  const cycleKey = yearlyCycle ? 'cycleYearly' : 'cycleMonthly';
  const nextCharge = data?.next_renewal_date
    ? new Date(data.next_renewal_date).toLocaleDateString(i18n.language)
    : null;

  // Hosted inside the titled modal: a wallet-style card, labeled facts, and
  // the action in the footer — not the settings block's scraps.
  if (framed) {
    return (
      <div
        data-testid="payment-method-framed"
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}
      >
        <Text
          component="span"
          style={{ fontSize: 'var(--text-small)', color: 'var(--text-secondary)' }}
        >
          {t('subscription.payment.caption')}
        </Text>

        {paymentMethod ? (
          <div
            style={{
              display: 'flex', flexDirection: 'column', gap: 'var(--space-5)',
              padding: 'var(--space-5)', borderRadius: 'var(--radius-xl)',
              border: '1px solid var(--border-subtle)',
              background: 'var(--surface-raised)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Text
                component="span"
                style={{
                  fontFamily: 'var(--font-family-alt-2)', fontSize: 'var(--text-lg)',
                  fontWeight: 'var(--font-bold)', color: 'var(--text-primary)',
                  letterSpacing: '0.08em',
                }}
              >
                {`•••• •••• •••• ${paymentMethod.last_four}`}
              </Text>
              <CardBrandLogo brand={paymentMethod.brand} />
            </div>
            {paymentMethod.holder && (
              <Text
                component="span"
                style={{
                  fontSize: 'var(--text-meta)', color: 'var(--text-tertiary)',
                  letterSpacing: '0.06em', textTransform: 'uppercase',
                }}
              >
                {paymentMethod.holder}
              </Text>
            )}
          </div>
        ) : (
          <Text component="span" style={{ fontSize: 'var(--text-small)', color: 'var(--text-tertiary)' }}>
            {t('subscription.payment.noMethod')}
          </Text>
        )}

        <div style={{ display: 'flex', flexDirection: 'column' }}>
          {data?.billing?.price_cents != null && (
            <FactRow
              label={t('subscription.payment.factSubscription')}
              value={t(
                yearlyCycle
                  ? 'subscription.payment.cycleValueYearly'
                  : 'subscription.payment.cycleValueMonthly',
                { price: money(data.billing.price_cents) },
              )}
            />
          )}
          {nextCharge && (
            <FactRow label={t('subscription.payment.factNextCharge')} value={nextCharge} last />
          )}
        </div>

        {banner && (
          <Text
            data-testid="payment-card-banner"
            component="span"
            style={{
              fontSize: 'var(--text-meta)',
              color: banner.type === 'success' ? 'var(--status-success-text)' : 'var(--status-error-text)',
            }}
          >
            {banner.message}
          </Text>
        )}
        {session.isError && (
          <Text
            data-testid="payment-session-error"
            component="span"
            style={{ fontSize: 'var(--text-meta)', color: 'var(--status-error-text)' }}
          >
            {t('subscription.payment.sessionError')}
          </Text>
        )}

        {paymentMethod && canChangePayment && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <UnstyledButton
              type="button"
              data-testid="change-payment-btn"
              disabled={session.isLoading}
              onClick={handleChangePayment}
              style={{
                padding: 'var(--space-2) var(--space-4)',
                borderRadius: 'var(--radius-full)',
                background: 'var(--seed-base)', color: 'var(--text-on-seed, #fff)',
                fontSize: 'var(--text-small)', fontWeight: 'var(--font-bold)',
                cursor: 'pointer', opacity: session.isLoading ? 0.6 : 1,
              }}
            >
              {t('subscription.payment.changePayment')}
            </UnstyledButton>
          </div>
        )}

        <Modal
          opened={isModalOpen}
          onClose={handleCancel}
          title={t('subscription.payment.iframeTitle')}
          size="5xl"
        >
          {renderModalBody()}
        </Modal>
      </div>
    );
  }

  return (
    <SettingsBlock
      title={t('subscription.payment.title')}
      caption={t('subscription.payment.caption')}
      action={paymentMethod && canChangePayment
        ? {
          label: t('subscription.payment.changePayment'),
          onClick: handleChangePayment,
          disabled: session.isLoading,
          testId: 'change-payment-btn',
        }
        : undefined}
      testId="subscription-payment-block"
    >
      {banner && (
        <div
          data-testid="payment-card-banner"
          style={{
            fontSize: 'var(--text-body-sm)',
            color: banner.type === 'success' ? 'var(--status-success-text, #15803d)' : 'var(--status-error-text, #dc2626)',
            marginBottom: 'var(--space-3)',
          }}
        >
          {banner.message}
        </div>
      )}
      {paymentMethod ? (
        <div className="mt-(--space-5) flex flex-col gap-(--space-3)">
          <div className="flex items-center gap-(--space-3)">
            <CardBrandLogo brand={paymentMethod.brand} />
            <div>
              <div
                style={{
                  fontWeight: 600,
                  color: 'var(--text-primary)',
                  fontSize: 'var(--text-body-sm)',
                }}
              >
                {t('subscription.payment.creditCard', { lastFour: paymentMethod.last_four })}
              </div>
              {paymentMethod.holder && (
                <div style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary)' }}>
                  {paymentMethod.holder}
                </div>
              )}
            </div>
          </div>
          {(nextCharge || data?.billing?.price_cents != null) && (
            <div
              data-testid="payment-card-billing-facts"
              style={{
                display: 'flex', flexDirection: 'column', gap: 'var(--space-1)',
                paddingTop: 'var(--space-2)',
                borderTop: '1px solid var(--border-ultra-subtle)',
                fontSize: 'var(--text-meta)', color: 'var(--text-secondary)',
              }}
            >
              {data?.billing?.price_cents != null && (
                <Text component="span">
                  {t(`subscription.payment.${cycleKey}`, { price: money(data.billing.price_cents) })}
                </Text>
              )}
              {nextCharge && (
                <Text component="span">{t('subscription.payment.nextChargeOn', { date: nextCharge })}</Text>
              )}
            </div>
          )}
          {!canChangePayment && (
            <div style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary)' }}>
              {t('subscription.payment.unavailableForStatus')}
            </div>
          )}
          {session.isError && (
            <div
              data-testid="payment-session-error"
              style={{ fontSize: 'var(--text-meta)', color: 'var(--status-error-text, #dc2626)' }}
            >
              {t('subscription.payment.sessionError')}
            </div>
          )}
        </div>
      ) : (
        <div
          style={{
            marginTop: 'var(--space-5)',
            fontSize: 'var(--text-body-sm)',
            color: 'var(--text-tertiary)',
          }}
        >
          {t('subscription.payment.noMethod')}
        </div>
      )}
      <Modal
        opened={isModalOpen}
        onClose={handleCancel}
        title={t('subscription.payment.iframeTitle')}
        size="5xl"
      >
        {renderModalBody()}
      </Modal>
    </SettingsBlock>
  );
}
